/// <reference types="@types/segment-analytics" />
import posthog from "posthog-js";
import { v4 as uuidv4 } from "uuid";

import { META_STANDARD_EVENT_MAPPING, shouldMirrorEvent, MirroredEvent } from "./analytics/events";

/**
 * Segment analytics - extends window object for analytics
 */
declare global {
  interface Window {
    analytics: any;
    fbq?: (...args: any[]) => void;
    metaPixelInitialized?: boolean;
    ttq?: {
      track?: (eventName: string, properties?: Record<string, any>) => void;
      page?: (...args: any[]) => void;
    };
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

const DEFAULT_DEV_POSTHOG_KEY = "XXXXXXXX";

const DEFAULT_DEV_SEGMENT_WRITE_KEY = "GSQnEh2JIoyHSbcfXyOwyXJKqQZgPbdT";

const META_COOKIE_FBC = "_fbc";
const META_COOKIE_FBP = "_fbp";
let gtagInitialized = false;
const GOOGLE_ADS_ID = 'AW-11175389439';

function initializeGtag(measurementId?: string) {
  if (typeof window === "undefined") {
    return;
  }

  if (gtagInitialized) {
    return;
  }

  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  const tagId = measurementId || GOOGLE_ADS_ID;
  const scriptId = `gtag-js-${tagId}`;
  if (!document.getElementById(scriptId)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
    script.id = scriptId;
    document.head.appendChild(script);
  }

  window.gtag =
    window.gtag ||
    function gtag(...args: any[]) {
      window.dataLayer?.push(args);
    };

  window.gtag("js", new Date());
  if (measurementId) window.gtag("config", measurementId, { send_page_view: false });
  window.gtag("config", GOOGLE_ADS_ID, { send_page_view: false });

  gtagInitialized = true;
}

function getCookieValue(name: string) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : undefined;
}

function enrichWithMetaCookies<T extends Record<string, any>>(properties: T) {
  if (typeof document === "undefined") {
    return properties;
  }

  return {
    ...properties,
    fbc: properties?.fbc ?? getCookieValue(META_COOKIE_FBC),
    fbp: properties?.fbp ?? getCookieValue(META_COOKIE_FBP),
  };
}

/** loads Segment analytics - script in public/index.html */
export const loadAnalytics = () => {
  // If window.analytics is already defined, return immediately
  // if (window.analytics) {
  //   console.log("[analytics] Analytics is already loaded");
  //   return;
  // }

  // TODO: move into env var
  // Load Segment Analytics
  console.log("loading analytics ", process.env.REACT_APP_SEGMENT_WRITE_KEY);
  if (window.analytics?.load) {
    window.analytics.load(
      process.env.REACT_APP_SEGMENT_WRITE_KEY || DEFAULT_DEV_SEGMENT_WRITE_KEY,
      { initialPageview: false, autoPageview: false }
    );
  }
  // load post hog
  posthog.init(process.env.REACT_APP_POSTHOG_KEY ?? DEFAULT_DEV_POSTHOG_KEY, {
    api_host: "https://app.posthog.com",
  });

  // load Meta Pixel if available
  const metaPixelId = process.env.REACT_APP_META_PIXEL_ID;
  if (metaPixelId && typeof window.fbq === "function") {
    if (!window.metaPixelInitialized) {
      window.fbq("init", metaPixelId);
      window.fbq("track", "PageView");
      window.metaPixelInitialized = true;
    }
  } else if (metaPixelId) {
    console.warn("[analytics] Meta Pixel script not yet loaded");
  }

  // load Google Analytics (GA4 via gtag)
  const measurementId = process.env.REACT_APP_GA_TRACKING_ID;
  initializeGtag(measurementId);
};

export const page = (category?: any, pathname?: any) => {
  if (!category || !pathname) {
    return;
  }

  console.log("[analytics] tracking page", pathname, category);
  window.analytics?.page?.(category, pathname);

  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_path: pathname,
      page_title: category,
    });
  }

  // NOTE: use usePageTracking hook instead
  // ReactGA.pageview(window.location.pathname + window.location.search);
};

export const identify = (userId: string, traits?: any) => {
  window.analytics?.identify?.(userId, traits);

  // identify with posthog
  posthog.identify(userId, traits);
};

export const reset = () => {
  window.analytics?.reset?.();
};

/** The existing Google Ads signup action is secondary; purchases drive bidding. */
export const trackGoogleAdsSignup = (loginsCount: number, userId?: string) => {
  if (loginsCount !== 1 || !userId || typeof window.gtag !== "function") return;
  // Persist an opaque receipt per account, so reloads deduplicate without
  // sending an Auth0 identifier or email to Google as a transaction ID.
  const key = `google_ads_signup_receipt:${userId}`;
  let transactionId: string;
  try {
    transactionId = localStorage.getItem(key) || uuidv4();
    localStorage.setItem(key, transactionId);
  } catch {
    return; // Cannot safely deduplicate a first-login page reload.
  }
  window.gtag("event", "conversion", {
    send_to: "AW-11175389439/enzbCJWv-8UbEP_R69Ap",
    transaction_id: transactionId,
    value: 0,
    currency: "GBP",
  });
};

/** segment and GA track call - should be used for interaction events i.e. user clicked button or initiated action
 * https://segment.com/academy/collecting-data/the-anatomy-of-a-track-call/
 *
 *
 */

export const trackEvent = (eventName: string, properties: any) => {
  const enrichedProperties = enrichWithMetaCookies({
    ...properties,
    event_id: properties?.event_id ?? uuidv4(),
    source: properties?.source ?? "web",
  });

  // Track with Segment
  window.analytics?.track?.(eventName, enrichedProperties);

  // Track with Posthog
  posthog.capture(eventName, enrichedProperties);

  const metaEvent = META_STANDARD_EVENT_MAPPING[eventName];
  if (metaEvent) {
    trackMetaPixelEvent(metaEvent, enrichedProperties);
  }

  if (typeof window.gtag === "function") {
    const { event_id: eventId, ...gtagProps } = enrichedProperties;
    window.gtag("event", eventName, {
      ...gtagProps,
      event_id: eventId,
    });
  }

  trackTikTokEvent(eventName, enrichedProperties);

  // Mirror key conversion events to our DB
  if (shouldMirrorEvent(eventName)) {
    // Infer status from event name pattern
    let status: "attempt" | "success" | "failed" | "viewed" = "success";
    if (eventName.endsWith("_failed") || eventName.includes("_error")) {
      status = "failed";
    } else if (eventName.endsWith("_started") || eventName.includes("_initiated")) {
      status = "attempt";
    } else if (eventName.endsWith("_shown") || eventName.includes("_viewed")) {
      status = "viewed";
    }
    mirrorEventToDb(eventName as MirroredEvent, enrichedProperties, status);
  }

  return enrichedProperties.event_id;
};

export const trackMetaPixelEvent = (
  eventName: string,
  properties?: Record<string, any>
) => {
  if (typeof window.fbq !== "function") {
    console.warn("[analytics] Meta Pixel is unavailable");
    return;
  }

  const { event_id: eventId, ...metaProperties } = properties || {};
  const metaConfig = eventId ? { eventID: eventId } : undefined;

  window.fbq("track", eventName, metaProperties, metaConfig);
};

export const trackTikTokEvent = (
  eventName: string,
  properties?: Record<string, any>
) => {
  if (!window.ttq || typeof window.ttq.track !== "function") {
    return;
  }

  window.ttq.track(eventName, properties);
};

/**
 * Mirror key events to our DB for conversion tracking.
 * Non-blocking - failures don't affect main analytics flow.
 * Uses axios which already has auth token configured.
 */
const mirrorEventToDb = async (
  eventName: MirroredEvent,
  properties: Record<string, any>,
  status: "attempt" | "success" | "failed" | "viewed" = "success"
) => {
  try {
    // Dynamic import to avoid circular deps
    const { axios } = await import("./axios");

    // Skip if axios doesn't have auth token set yet
    if (!axios.defaults.headers.common["Authorization"]) {
      return;
    }

    await axios.post("/activity", {
      event: eventName,
      status,
      metadata: {
        platform: "web",
        ...properties,
      },
      error_code: properties?.error_code,
      error_message: properties?.error_message,
      duration_ms: properties?.duration_ms,
    });
  } catch (error) {
    // Non-blocking - just log and continue
    console.warn("[analytics] Failed to mirror event to DB:", error);
  }
};

// export const trackEvent = (name: string, properties: any) => {
//   window.analytics.track(name, properties);

//   ReactGA.event({
//     category: "User",
//     action: name,
//     label: properties?.label,
//     value: properties?.value,
//   });
// };
